import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ProgressBar } from "@/components/common/ProgressBar";
import { UpdatesHistory } from "@/components/product/UpdatesHistory";
import { Pencil, CheckCircle2, RotateCcw, Trash2, Link2, AlertTriangle, Archive, ListChecks } from "lucide-react";
import { launchBadge, priorityBadge, isOverdue } from "@/data/launchOptions";
import { api } from "@/lib/api";

const Row = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5A6B8C]">{label}</p>
    <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#14245F]">{value?.toString().trim() ? value : <span className="text-[#9DAAC6]">Not set</span>}</p>
  </div>
);

const Stat = ({ label, value, tone }) => (
  <div className="rounded-lg border border-[#E4E9F3] bg-white p-2 text-center">
    <p className="font-numeric text-lg font-bold leading-none" style={{ color: tone }}>{value}</p>
    <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#5A6B8C]">{label}</p>
  </div>
);

export const MilestoneDetail = ({ milestone: m, dependencies = [], today, onOpenChange, onOpenMilestone, canWrite = true, onEdit, onComplete, onReopen, onArchive, onRestore, onDelete, onUpdatesChange }) => {
  const navigate = useNavigate();
  const [taskSummary, setTaskSummary] = useState(null);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [warnOpen, setWarnOpen] = useState(false);

  useEffect(() => {
    if (!m) { setTaskSummary(null); setLinkedTasks([]); return; }
    let active = true;
    api.milestoneTaskSummary(m.id).then((s) => { if (active) setTaskSummary(s); }).catch(() => {});
    api.list("tasks").then((all) => { if (active) setLinkedTasks(all.filter((x) => x.related_milestone_id === m.id)); }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.id]);

  if (!m) return null;
  const overdue = isOverdue(m, today);
  const openLinked = taskSummary ? taskSummary.outstanding : 0;

  const handleComplete = () => {
    if (openLinked > 0) { setWarnOpen(true); return; }
    onComplete(m);
  };
  const confirmComplete = async () => {
    setWarnOpen(false);
    onComplete(m);
    try { await api.milestoneCompleteConfirm(m.id); } catch (e) { console.error("Milestone complete audit failed", e); }
  };

  return (
    <Dialog open={!!m} onOpenChange={onOpenChange}>
      <DialogContent data-testid="milestone-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl border-[#E4E9F3] bg-white">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={launchBadge(m.status)} label={m.status} />
            <StatusBadge status={priorityBadge(m.priority)} label={m.priority} />
            {overdue && <StatusBadge status="error" label="Overdue" />}
          </div>
          <DialogTitle className="mt-2 font-heading text-xl font-semibold tracking-tight text-[#14245F]">{m.title}</DialogTitle>
          <DialogDescription className="text-[#5A6B8C]">{m.workstream || m.category} · {m.owner}{m.due_date ? ` · due ${m.due_date}` : ""} · weight {m.readiness_weight ?? 1}{m.off_framework ? " · off-framework" : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="mb-1 flex justify-between text-xs text-[#5A6B8C]"><span>Completion</span></div>
            <ProgressBar value={m.status === "Complete" ? 100 : m.completion} showLabel />
          </div>

          {taskSummary && taskSummary.total > 0 && (
            <div className="rounded-xl border border-[#E4E9F3] bg-[#F7F9FE] p-3" data-testid="milestone-task-summary">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#5A6B8C]"><ListChecks size={13} /> Linked tasks</span>
                <span className="text-xs font-bold text-[#0C8E8D]" data-testid="milestone-task-pct">{taskSummary.completion_pct}% complete</span>
              </div>
              <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-[#E4E9F3]">
                <div className="h-full rounded-full bg-[#0EA5A4]" style={{ width: `${taskSummary.completion_pct}%` }} />
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                <Stat label="Total" value={taskSummary.total} tone="#14245F" />
                <Stat label="Done" value={taskSummary.complete} tone="#16A34A" />
                <Stat label="Active" value={taskSummary.in_progress} tone="#06B6D4" />
                <Stat label="Open" value={taskSummary.outstanding} tone="#E0A82E" />
              </div>
              <p className="mt-2 text-[11px] text-[#9DAAC6]">Informational only — linked tasks do not affect launch readiness or milestone completion.</p>
            </div>
          )}

          {linkedTasks.length > 0 && (
            <div className="space-y-1.5" data-testid="milestone-linked-tasks">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5A6B8C]">Linked tasks · open in Tasks</p>
              {linkedTasks.slice(0, 8).map((tk) => (
                <button key={tk.id} type="button" data-testid={`open-linked-task-${tk.id}`} aria-label={`Open linked task: ${tk.title}`} onClick={() => navigate(`/?tab=tasks&task=${tk.id}`)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-[#E4E9F3] px-3 py-1.5 text-left text-sm transition-colors hover:border-[#0EA5A4]/50 hover:bg-[#0EA5A4]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A4]/40">
                  <span className="truncate text-[#14245F]">{tk.title}</span>
                  <StatusBadge status={tk.status === "Complete" ? "success" : "neutral"} label={tk.status} />
                </button>
              ))}
            </div>
          )}

          {dependencies.length > 0 && (
            <div className="space-y-1.5" data-testid="dependency-info">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#5A6B8C]"><Link2 size={13} /> Depends on ({dependencies.length})</div>
              {dependencies.map((dep) => {
                const incomplete = dep.status !== "Complete";
                return (
                  <button key={dep.id} type="button" onClick={() => onOpenMilestone?.(dep)} aria-label={`View dependency: ${dep.title}`} data-testid={`dependency-${dep.id}`} className={`w-full text-left rounded-xl border p-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]/40 ${incomplete ? "border-[#06B6D4]/40 bg-[#06B6D4]/10 hover:bg-[#06B6D4]/20" : "border-[#E4E9F3] bg-[#F7F9FE] hover:bg-[#EEF2FB]"}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-[#14245F]">{dep.title}</span>
                      <StatusBadge status={launchBadge(dep.status)} label={dep.status} />
                      {incomplete && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0E7490]"><AlertTriangle size={12} /> not complete</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {m.current_blocker && (
            <div className="flex items-start gap-2 rounded-xl bg-[#FB6F5A]/[0.08] px-3 py-2 text-sm font-medium text-[#C2410C]">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{m.current_blocker}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Row label="Description" value={m.description} />
            <Row label="Next action" value={m.next_action} />
            <Row label="Evidence / completion note" value={m.evidence} />
            <Row label="Notes" value={m.notes} />
          </div>

          {canWrite && (
          <div className="flex flex-wrap gap-2 border-t border-[#EEF2FB] pt-4">
            <Button data-testid="detail-edit-btn" onClick={() => onEdit(m)} className="rounded-full bg-[#E0A82E] text-white hover:bg-[#C79422]"><Pencil size={15} /> Edit</Button>
            {m.status !== "Complete" ? (
              <Button data-testid="detail-complete-btn" onClick={handleComplete} className="rounded-full bg-[#16A34A] text-white hover:bg-[#15803D]"><CheckCircle2 size={15} /> Mark Complete</Button>
            ) : (
              <Button data-testid="detail-reopen-btn" variant="outline" onClick={() => onReopen(m)} className="rounded-full border-[#E4E9F3]"><RotateCcw size={15} /> Reopen</Button>
            )}
            {m.archived ? (
              <Button data-testid="detail-restore-btn" variant="outline" onClick={() => onRestore(m)} className="rounded-full border-[#0EA5A4]/40 text-[#0EA5A4] hover:bg-[#0EA5A4]/10"><RotateCcw size={15} /> Restore</Button>
            ) : (
              <Button data-testid="detail-archive-btn" variant="outline" onClick={() => onArchive(m)} className="rounded-full border-[#E4E9F3] text-[#5A6B8C]"><Archive size={15} /> Archive</Button>
            )}
            <Button data-testid="detail-delete-btn" variant="outline" onClick={() => onDelete(m)} className="rounded-full border-[#E4E9F3] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={15} /> Delete</Button>
          </div>
          )}

          <div className="border-t border-[#EEF2FB] pt-4">
            <UpdatesHistory relatedId={m.id} relatedType="Launch Milestone" relatedName={m.title} area="Launch" onChange={onUpdatesChange} />
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={warnOpen} onOpenChange={setWarnOpen}>
        <AlertDialogContent data-testid="milestone-complete-warning" className="rounded-2xl border-[#E4E9F3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[#14245F]">Complete milestone with open tasks?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A6B8C]">
              This milestone still has <span className="font-semibold text-[#14245F]">{openLinked} open</span> linked task{openLinked === 1 ? "" : "s"}.
              Completing the milestone will not change those tasks. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="milestone-complete-cancel" className="rounded-full border-[#E4E9F3]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="milestone-complete-confirm" onClick={confirmComplete} className="rounded-full bg-[#16A34A] text-white hover:bg-[#15803D]">Confirm & complete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
